import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Route, useParams } from 'react-router';

import Redirect from '@swf/components/Redirect/Redirect';
import { FeatureFlag, RoutingKey } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { ClubRoute, UserClubRole } from '@swf/core/types/Poker';
import { useCheckUserClubRole, useGetClubMemberOptions, useGetCurrentClub } from '@swf/poker';

import ClubNav from '../Nav/ClubNav/ClubNav';

import Announcements from './Announcements/Announcements';
import type { ClubContextState } from './ClubContext/ClubContext';
import ClubContext, { initialState } from './ClubContext/ClubContext';
import styles from './Container.scss';
import Header from './Header/Header';
import Lobby from './Lobby/Lobby';
import Members from './Members/MembersContainer';
import Results from './Results/Results';
import ResultsContainer from './Results/v1/ResultsContainer';
import Settings from './Settings/Settings';
import Standings from './Standings/StandingsContainer';

export interface Props {
  path: string;
  loading: boolean;
}

const Component = {
  [ClubRoute.Announcements]: Announcements,
  [ClubRoute.Lobby]: Lobby,
  [ClubRoute.Results]: Results,
  [ClubRoute.Members]: Members,
  [ClubRoute.Settings]: Settings,
  [ClubRoute.Standings]: Standings
};

const Club: React.FC<Props> = ({ path }) => {
  const resultsEnabled = useFeature(FeatureFlag.PKRHomeGamesResults);
  const { clubId, section } = useParams<{ clubId: string; section: string }>();
  const { handleGetClubMemberOptions, loading: settingsLoading } = useGetClubMemberOptions();

  if (resultsEnabled) {
    Component[ClubRoute.Results] = ResultsContainer;
  }

  const PageComponent = Component[section];

  const club = useGetCurrentClub(clubId);
  const restrictAccess = useCheckUserClubRole(`${clubId}`, [
    UserClubRole.Confirmation,
    UserClubRole.Suspended
  ]);

  useEffect(() => {
    handleGetClubMemberOptions(Number(clubId));
  }, [clubId, handleGetClubMemberOptions]);

  if (isEmpty(club) || restrictAccess) {
    return <Redirect routingKey={RoutingKey.HomeGamesClubs} />;
  }

  return (
    <>
      <Header />
      <ClubNav />
      <div
        className={classNames(styles.container, {
          [`club-section-${section}`]: true
        })}
      >
        <Route
          path={path}
          render={(props) => <PageComponent {...props} loading={settingsLoading} />}
        />
      </div>
    </>
  );
};

const Container: React.FC<Props> = (props) => {
  const [state, setState] = useState<ClubContextState>(initialState);

  return (
    <ClubContext.Provider value={{ state, setState }}>
      <Club {...props} />
    </ClubContext.Provider>
  );
};

export default Container;
